import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import NamedBox from '../atoms/NamedBox';
import { useTableSort } from '../hooks/useTableSort';
import { SpecialTeamsParticipationsRow } from '../types/CollegeProductionData';

type ColumnAlign = 'center' | 'left';

interface ColumnConfig {
  key: keyof SpecialTeamsParticipationsRow;
  label: string;
  align: ColumnAlign;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'year', label: 'Year', align: 'center' },
  { key: 'specialTeamsType', label: 'Special Teams Phase', align: 'left' },
  { key: 'playCount', label: 'Play Count', align: 'center' },
];

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  headerName: {
    fontFamily: 'inherit',
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto' as const,
    backgroundColor: '#ffffff',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: '#ffffff',
    fontSize: theme.typography.pxToRem(13),
  },
  theadRow: {
    height: 40,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
  },
  th: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: '#1e1e1e',
    padding: theme.spacing(0.75, 1),
    whiteSpace: 'nowrap' as const,
    borderRight: '1px solid #f0f0f0',
    userSelect: 'none' as const,
    cursor: 'pointer',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  alignCenter: {
    textAlign: 'center' as const,
  },
  alignLeft: {
    textAlign: 'left' as const,
  },
  bodyRow: {
    height: 44,
    transition: 'background-color 0.15s ease',
    '&:nth-child(odd)': {
      backgroundColor: '#f5f5f5',
    },
    '&:nth-child(even)': {
      backgroundColor: '#ffffff',
    },
    '&:hover': {
      backgroundColor: '#f8f8f8',
    },
  },
  td: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: '#1e1e1e',
    padding: theme.spacing(0.75, 1),
    whiteSpace: 'nowrap' as const,
    borderRight: '1px solid #f0f0f0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  sortLabelIcon: {
    opacity: 1,
  },
}), { name: 'SpecialTeamsPlayerParticipationTable' });

interface SpecialTeamsPlayerParticipationTableProps {
  className?: string;
  data?: SpecialTeamsParticipationsRow[];
}

export default function SpecialTeamsPlayerParticipationTable(props: SpecialTeamsPlayerParticipationTableProps) {
  const { className, data } = props;
  const classes = useStyles();
  const { sortKey, sortDirection, handleSort, sortedRows } = useTableSort<SpecialTeamsParticipationsRow, keyof SpecialTeamsParticipationsRow>({
    rows: data,
    initialSortKey: 'year',
    initialDirection: 'desc',
    nextKeyDirection: 'desc',
  });

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      headerNameClassName={classes.headerName}
      name="Special Teams Player Participation"
      white
    >
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.theadRow}>
              {COLUMNS.map(column => {
                const alignClass = column.align === 'left' ? classes.alignLeft : classes.alignCenter;
                const isActive = column.key === sortKey;
                return (
                  <th key={column.key} className={clsx(classes.th, alignClass)}>
                    <TableSortLabel
                      active={isActive}
                      direction={isActive ? sortDirection : 'asc'}
                      hideSortIcon={false}
                      IconComponent={(iconProps) => (
                        <ArrowDownwardIcon
                          {...iconProps}
                          style={{
                            color: isActive ? '#2f6bff' : '#b5b5b5',
                          }}
                        />
                      )}
                      classes={{ icon: classes.sortLabelIcon }}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => (
              <tr key={`${row.year}-${row.specialTeamsType}-${index}`} className={classes.bodyRow}>
                {COLUMNS.map(column => {
                  const alignClass = column.align === 'left' ? classes.alignLeft : classes.alignCenter;
                  return (
                    <td key={column.key} className={clsx(classes.td, alignClass)}>
                      {row[column.key]}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </NamedBox>
  );
}
